// N5 — 앱이 꺼져 있어도 자동 탑승 · 네이티브 `BuslinkTagless` 플러그인 브리지 (2026-10-11 · 설계 `.claude/native-tagless-background-design.md`)
// Firebase import 금지 — CF 호출(키 발급·폐기)은 호출부가 issue/revoke 함수로 넘긴다(테스트가 vm 에 소스째 태운다).
//
// 🔴 판정 주체는 1개: 이 플러그인이 있는 앱(N5-b Android 1.2 · N5-c iOS 1.1)은 웹 JS 감시(taglessWatcher)를 켜지 않는다.
//    감지·전송은 네이티브가 앱이 꺼져 있어도 하고, 확정은 서버 평가기(taglessEvaluate)가 한다.
// 🔴 옛 앱(플러그인 없음)·브라우저는 hasNativePlugin false → 이 파일의 어떤 함수도 아무것도 하지 않는다.
// 🔴 옵트인 기본 꺼짐 — 켜짐 상태의 정본은 네이티브(키가 Keychain/EncryptedSharedPreferences 에 있는가).
//
// 플러그인 계약(N5-b/c 가 구현) — 메서드 5개:
//   requestPermissions() → { granted:boolean, background:boolean }   위치(정확)·블루투스·알림·«항상 허용»
//   enable({ cid, keyId, secret, endpoint, uuid, windows, expiresAt, empNo }) → {}   키 보관 + 감시 시작
//   disable() → {}                                                   키 삭제 + 감시 중지
//   status() → { enabled:boolean, empNo?:string, cid?:string, expiresAt?:number }
//   openBatterySettings() → {}                                       (Android 전용 · 절전 예외 안내)
import { hasNativePlugin, nativeCall } from "./nativeApp";

export const NATIVE_TAGLESS_PLUGIN = "BuslinkTagless";
// 서버 functions/taglessBackgroundPolicy.js TAGLESS_KEY_RENEW_DAYS 거울(테스트가 대조한다).
export const TAGLESS_KEY_RENEW_DAYS = 30;

export function isNativeTaglessAvailable() {
  return hasNativePlugin(NATIVE_TAGLESS_PLUGIN);
}

export function nativeTaglessPlatform() {
  try {
    const p = window.Capacitor && typeof window.Capacitor.getPlatform === "function" ? window.Capacitor.getPlatform() : "";
    return p === "android" || p === "ios" ? p : "";
  } catch {
    return "";
  }
}

/** D9 — 남은 기간이 30일 미만(또는 모름)이면 재발급. */
export function shouldRenewNativeKey(expiresAt, nowMs) {
  const e = Number(expiresAt);
  if (!Number.isFinite(e) || e <= 0) return true;
  return e - nowMs < TAGLESS_KEY_RENEW_DAYS * 86400000;
}

/** 지금 이 사번으로 켜져 있는가. 🔴 플러그인 없음·오류·다른 사번 = 꺼짐(안전한 쪽). */
export async function getNativeTaglessStatus(empNo) {
  if (!isNativeTaglessAvailable()) return { enabled: false };
  try {
    const s = await nativeCall(NATIVE_TAGLESS_PLUGIN, "status");
    const enabled = !!(s && s.enabled) && (!empNo || String(s.empNo || "") === String(empNo));
    return { enabled, empNo: s && s.empNo, expiresAt: s && s.expiresAt };
  } catch {
    return { enabled: false };
  }
}

async function permissionsOk() {
  const r = await nativeCall(NATIVE_TAGLESS_PLUGIN, "requestPermissions");
  if (!r || !r.granted) return { ok: false, message: "위치·블루투스 권한을 허용해야 자동 탑승을 켤 수 있습니다" };
  if (!r.background) return { ok: false, message: "위치 권한을 «항상 허용» 으로 바꿔야 앱을 닫아 두어도 자동 탑승이 됩니다" };
  return { ok: true };
}

/**
 * 켜기 — 권한 → 서버 키 발급(issue) → 네이티브 보관·감시 시작.
 * @param {{ issue: () => Promise<object>, empNo: string }} p  issue = CF taglessIssueDeviceKey 호출
 * @returns {Promise<{ok:boolean, message?:string}>}
 */
export async function enableNativeTagless({ issue, empNo }) {
  if (!isNativeTaglessAvailable()) return { ok: false, message: "이 앱 버전에서는 사용할 수 없습니다" };
  try {
    const perm = await permissionsOk();
    if (!perm.ok) return perm;
    const key = await issue();
    if (!key || !key.secret || !key.keyId) return { ok: false, message: "자동 탑승 키를 받지 못했습니다" };
    await nativeCall(NATIVE_TAGLESS_PLUGIN, "enable", { ...key, empNo: String(empNo || "") });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: (e && e.message) || "자동 탑승을 켜지 못했습니다" };
  }
}

/** 끄기 — 네이티브 키 삭제가 먼저(서버 폐기가 실패해도 기기는 확실히 멈춘다). */
export async function disableNativeTagless({ revoke }) {
  if (!isNativeTaglessAvailable()) return { ok: true };
  try { await nativeCall(NATIVE_TAGLESS_PLUGIN, "disable"); } catch (_) { /* 이미 꺼짐 */ }
  try { if (revoke) await revoke(); } catch (_) { /* 서버 폐기 실패 — 키는 기기에 없다 */ }
  return { ok: true };
}

/** D9 — 앱을 열 때 남은 기간이 30일 미만이면 조용히 재발급(실패해도 화면에 아무것도 안 띄운다). */
export async function renewNativeTaglessIfNeeded({ issue, empNo, nowMs }) {
  const st = await getNativeTaglessStatus(empNo);
  if (!st.enabled || !shouldRenewNativeKey(st.expiresAt, nowMs)) return { renewed: false };
  try {
    const key = await issue();
    if (!key || !key.secret) return { renewed: false };
    await nativeCall(NATIVE_TAGLESS_PLUGIN, "enable", { ...key, empNo: String(empNo || "") });
    return { renewed: true };
  } catch {
    return { renewed: false };
  }
}

/**
 * Android 전용 — 절전 예외 목록 화면 열기(삼성 «잠자는 앱» 등이 상주 감시를 끄지 않게 · 설계 -bg-android.md §5).
 * iOS·브라우저·플러그인 없는 앱 = false(아무것도 안 한다).
 */
export async function openNativeTaglessBatterySettings() {
  if (!isNativeTaglessAvailable() || nativeTaglessPlatform() !== "android") return false;
  try {
    await nativeCall(NATIVE_TAGLESS_PLUGIN, "openBatterySettings");
    return true;
  } catch {
    return false;
  }
}

/** 오늘 boardings 중 이 경로(native-bg)가 만든 것만 — «아니에요» 목록. */
export function pickNativeTaglessBoardings(rows) {
  return (rows || []).filter((r) => r && r.via === "tagless" && r.taglessPath === "native-bg" && r.candidateId);
}
